import { makeAutoObservable } from "mobx";

import { IChatExchangeError } from "../models/interfaces/chat-exchange-error.interface";
import { IChatThreadExtendedModel } from "../models/interfaces/chat-thread-pending-model.interface";

export class ThreadStore {
  private _threads: IChatThreadExtendedModel[] = [];
  private _activeThread: IChatThreadExtendedModel | undefined;
  private _threadErrors: IChatExchangeError[] = [];

  public loadingThreads: boolean = false;

  public pendingCopyResponseToInput?: string;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public get threads(): IChatThreadExtendedModel[] {
    return this._threads;
  }

  public set threads(threads: IChatThreadExtendedModel[]) {
    this._threads = threads;
  }

  public get activeThread(): IChatThreadExtendedModel | undefined {
    return this._activeThread;
  }

  public get threadErrors(): IChatExchangeError[] {
    return this._threadErrors;
  }

  public set threadErrors(errors: IChatExchangeError[]) {
    this._threadErrors = errors;
  }

  public get hasThreadErrors(): boolean {
    return this._threadErrors.length > 0;
  }

  public triggerCopyResponseToInput(input: string | undefined): void {
    this.pendingCopyResponseToInput = input;
  }

  public setActiveThread(thread: IChatThreadExtendedModel): void {
    this._activeThread = thread;
  }

  public resetActiveThread(): void {
    this._activeThread = undefined;
  }

  public removeThreadById(threadId: string): void {
    this._threads = this._threads.filter(thread => thread.id !== threadId);

    if (this._activeThread?.id === threadId) {
      this.resetActiveThread();
    }
  }

  // NOTE #63781 Called after the BE response is received to persist the final thread state. Updates the activeThread if it matches, then either updates the existing thread or, for a new thread that does not exist yet, adds it in the _threads list. A missing thread is expected here (new thread), so it is not treated as an error.
  public updateOrCreatePendingThread(updatedPendingThread: IChatThreadExtendedModel): void {
    if (this._activeThread?.id === updatedPendingThread.id) {
      this.setActiveThread(updatedPendingThread);
    }

    const index = this._threads.findIndex(t => t.id === updatedPendingThread.id);
    if (index !== -1) {
      // NOTE #63781 If the thread exists, update it
      this._threads[index] = updatedPendingThread;
    } else {
      this._threads.push(updatedPendingThread); // NOTE #63781 If the thread does not exist, add it to the list
    }
  }

  // NOTE #63781 Called before the BE response to reflect a submitted prompt in a pending thread. Sets the activeThread (when it matches threadId or when the request comes from a new thread) and updates pendingPrompt on the existing thread so the pending state shows immediately. The final state is written later by updateOrCreatePendingThread once the BE responds.
  public setPendingPromptInThread(threadId: string | undefined, pendingPrompt: string | undefined): void {
    // NOTE #63781 If the active thread is the same as the one being updated, or if there is no active thread (this._activeThread?.id===undefined and threadId===undefined), set here activeThread
    if (this._activeThread?.id === threadId) {
      this.setActiveThread({ ...this._activeThread, pendingPrompt } as IChatThreadExtendedModel);
    }

    const index = this._threads.findIndex(t => t.id === threadId);
    if (index !== -1) {
      // NOTE #63781 If the thread exists, update it
      this._threads[index].pendingPrompt = pendingPrompt;
    }
  }

  // NOTE #63781 Called before the BE response when a user cancels a running exchange. Sets the activeThread (when it matches threadId or when the request comes from a new thread) and updates isExchangeCancelled on the existing thread so the cancelled state shows immediately. The final state is written later by updateOrCreatePendingThread once the BE responds.
  public setIsExchangeCancelledInThread(threadId: string | undefined, isExchangeCancelled: boolean): void {
    if (this._activeThread?.id === threadId) {
      // NOTE #63781 If the active thread is the same as the one being updated, or if there is no active thread (this._activeThread?.id===undefined and threadId===undefined), set here activeThread
      this.setActiveThread({ ...this._activeThread, id: threadId, isExchangeCancelled } as IChatThreadExtendedModel);
    }

    const index = this._threads.findIndex(t => t.id === threadId);
    if (index !== -1) {
      // NOTE #63781 If the thread exists, update it
      this._threads[index].isExchangeCancelled = isExchangeCancelled;
    }
  }

  public setModelIdInThread(threadId: string | undefined, modelId: string | undefined): void {
    if (this._activeThread?.id === threadId) {
      this.setActiveThread({ ...this._activeThread, id: threadId, modelId } as IChatThreadExtendedModel);
    }

    const index = this._threads.findIndex(t => t.id === threadId);
    if (index !== -1) {
      this._threads[index].modelId = modelId;
    }
  }
}
